
import React, { useState, useEffect } from 'react';
import { Icons } from '../ui/Icons';
import { database, subscribeToPath } from '../../services/database';
import { DailyNightAuditReport, ReportPermissionLevel } from '../../types';
import { toast } from 'react-hot-toast';
import { safeFormat } from '../../utils/safeDateFns';

interface NightAuditDailyReportProps {
    staffName: string;
    permissionLevel?: ReportPermissionLevel;
    initialLevel?: 'form' | 'preview' | 'history';
    onLevelChange?: (level: 'form' | 'preview' | 'history') => void;
}

const NightAuditDailyReport: React.FC<NightAuditDailyReportProps> = ({ 
    staffName, 
    permissionLevel = 'view',
    initialLevel = 'form',
    onLevelChange
}) => {
    const canGenerate = permissionLevel === 'generate' || permissionLevel === 'export';
    const canExport = permissionLevel === 'export';

    const [reports, setReports] = useState<DailyNightAuditReport[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentLevel, setCurrentLevel] = useState<'form' | 'preview' | 'history'>(initialLevel);
    const [selectedReport, setSelectedReport] = useState<DailyNightAuditReport | null>(null);

    useEffect(() => {
        setCurrentLevel(initialLevel);
    }, [initialLevel]);

    const handleLevelChange = (level: 'form' | 'preview' | 'history') => {
        setCurrentLevel(level);
        if (onLevelChange) onLevelChange(level);
    };

    // Form State
    const [formData, setFormData] = useState({
        inHouseRooms: '',
        compRooms: '',
        ullivikNoShowAssignedCount: '',
        ullivikNoShowAssignedRooms: '',
        ullivikNoShowKeepCount: '',
        ullivikNoShowKeepRooms: '',
        regularNoShowCount: '',
        regularNoShowRooms: '',
        ullivikDepartureCount: '',
        ullivikDepartureRooms: '',
        dndRoomsCount: '',
        dndRoomsRooms: '',
    });

    const [roomChanges, setRoomChanges] = useState<{ oldRoom: string; newRoom: string }[]>([
        { oldRoom: '', newRoom: '' }
    ]);

    const [generatedReport, setGeneratedReport] = useState<DailyNightAuditReport | null>(null);

    // Date Logic: Always Yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    const dateStrFormatted = safeFormat(yesterday, 'EEEE, MMMM d, yyyy');
    const dateKey = yesterday.toISOString().split('T')[0];

    useEffect(() => {
        const unsub = subscribeToPath('dailyNightAuditReports', (data) => {
            if (data) {
                const list = Object.values(data) as DailyNightAuditReport[];
                setReports(list.sort((a, b) => {
                    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                    return dateB - dateA;
                }));
            }
            setIsLoading(false);
        });
        return () => unsub();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRoomChangeUpdate = (index: number, field: 'oldRoom' | 'newRoom', value: string) => {
        const updated = [...roomChanges];
        updated[index] = { ...updated[index], [field]: value };
        setRoomChanges(updated);
    };

    const addRoomChange = () => {
        setRoomChanges([...roomChanges, { oldRoom: '', newRoom: '' }]);
    };

    const removeRoomChange = (index: number) => {
        if (roomChanges.length === 1) {
            setRoomChanges([{ oldRoom: '', newRoom: '' }]);
            return;
        }
        setRoomChanges(roomChanges.filter((_, i) => i !== index));
    };

    const validateCounts = () => {
        const parseRooms = (str: string) => str.split('/').map(r => r.trim()).filter(r => r.length > 0);

        // Validation for Ullivik No Show Assigned
        if (formData.ullivikNoShowAssignedCount && formData.ullivikNoShowAssignedRooms) {
            const count = parseInt(formData.ullivikNoShowAssignedCount);
            if (parseRooms(formData.ullivikNoShowAssignedRooms).length !== count) {
                return `Mismatch: No. of Ullivik No show assigned names (${count}) does not match room count entered.`;
            }
        }

        // Validation for Ullivik No Show Keep
        if (formData.ullivikNoShowKeepCount && formData.ullivikNoShowKeepRooms) {
            const count = parseInt(formData.ullivikNoShowKeepCount);
            if (parseRooms(formData.ullivikNoShowKeepRooms).length !== count) {
                return `Mismatch: No. of Keep for Ullivik No show (${count}) does not match room count entered.`;
            }
        }

        // Validation for Regular No Show
        if (formData.regularNoShowCount && formData.regularNoShowRooms) {
            const count = parseInt(formData.regularNoShowCount);
            if (parseRooms(formData.regularNoShowRooms).length !== count) {
                return `Mismatch: Regular guest no-show rooms (${count}) does not match room count entered.`;
            }
        }

        // Validation for Ullivik Departure
        if (formData.ullivikDepartureCount && formData.ullivikDepartureRooms) {
            const count = parseInt(formData.ullivikDepartureCount);
            if (parseRooms(formData.ullivikDepartureRooms).length !== count) {
                return `Mismatch: Ullivik Departure (${count}) does not match room count entered.`;
            }
        }

        // Validation for DND
        if (formData.dndRoomsCount && formData.dndRoomsRooms) {
            const count = parseInt(formData.dndRoomsCount);
            if (parseRooms(formData.dndRoomsRooms).length !== count) {
                return `Mismatch: Don’t Disturb Room (${count}) does not match room count entered.`;
            }
        }

        return null;
    };

    const handleGenerate = async () => {
        const error = validateCounts();
        if (error) {
            toast.error(error);
            return;
        }

        // Check for duplicates
        if (reports.some(r => r.date === dateKey)) {
            toast.error(`A report for ${dateKey} already exists in history.`);
            return;
        }

        const formatRooms = (str: string) => {
            if (!str) return "";
            const rooms = str.split('/').map(r => r.trim()).filter(r => r.length > 0);
            return `(${rooms.join(' / ')})`;
        };

        const newReport: DailyNightAuditReport = {
            id: `dna_${Date.now()}`,
            date: dateKey,
            displayDate: `Date — ${dateStrFormatted}`,
            inHouseRooms: parseInt(formData.inHouseRooms) || 0,
            compRooms: parseInt(formData.compRooms) || 0,
            ullivikNoShowAssigned: {
                count: parseInt(formData.ullivikNoShowAssignedCount) || 0,
                details: formatRooms(formData.ullivikNoShowAssignedRooms)
            },
            ullivikNoShowKeep: {
                count: parseInt(formData.ullivikNoShowKeepCount) || 0,
                details: formatRooms(formData.ullivikNoShowKeepRooms)
            },
            regularNoShow: {
                count: parseInt(formData.regularNoShowCount) || 0,
                details: formatRooms(formData.regularNoShowRooms)
            },
            roomChanges: roomChanges.map(rc => ({
                oldRoom: rc.oldRoom,
                newRoom: rc.newRoom
            })).filter(rc => rc.oldRoom || rc.newRoom),
            ullivikDeparture: {
                count: parseInt(formData.ullivikDepartureCount) || 0,
                details: formatRooms(formData.ullivikDepartureRooms)
            },
            dndRooms: {
                count: parseInt(formData.dndRoomsCount) || 0,
                details: formatRooms(formData.dndRoomsRooms)
            },
            createdAt: new Date().toISOString(),
            createdBy: staffName
        };

        try {
            await database.saveDailyNightAuditReport(newReport);
            setSelectedReport(newReport);
            handleLevelChange('preview');
            toast.success('Report generated and saved to history');
        } catch (err) {
            toast.error('Failed to save report');
        }
    };

    const handleReset = () => {
        setFormData({
            inHouseRooms: '',
            compRooms: '',
            ullivikNoShowAssignedCount: '',
            ullivikNoShowAssignedRooms: '',
            ullivikNoShowKeepCount: '',
            ullivikNoShowKeepRooms: '',
            regularNoShowCount: '',
            regularNoShowRooms: '',
            ullivikDepartureCount: '',
            ullivikDepartureRooms: '',
            dndRoomsCount: '',
            dndRoomsRooms: '',
        });
        setRoomChanges([{ oldRoom: '', newRoom: '' }]);
        setGeneratedReport(null);
    };

    const copyAsText = (report: DailyNightAuditReport) => {
        const roomChangesText = report.roomChanges.map(rc => `Change rooms: ${rc.oldRoom} to ${rc.newRoom}`).join('\n');
        const text = `
${report.displayDate}
No. of in-house rooms including Day Use: ${report.inHouseRooms}
No. of complimentary rooms: ${report.compRooms}
No. of Ullivik No show assigned names: ${report.ullivikNoShowAssigned.count} ${report.ullivikNoShowAssigned.details}
No. of Keep for Ullivik No show: ${report.ullivikNoShowKeep.count} ${report.ullivikNoShowKeep.details}
Regular guest no-show rooms: ${report.regularNoShow.count} ${report.regularNoShow.details}
${roomChangesText}
Ullivik Departure: ${report.ullivikDeparture.count} ${report.ullivikDeparture.details}
Don’t Disturb Room: ${report.dndRooms.count} ${report.dndRooms.details}
        `.trim();
        navigator.clipboard.writeText(text);
        toast.success('Copied as text');
    };

    const copyAsTable = (report: DailyNightAuditReport) => {
        const roomChangesHtml = report.roomChanges.map(rc => `
            <tr>
                <td style="padding: 12px; border: 1px solid #e2e8f0; color: #64748b; font-style: italic;">Room Change Action</td>
                <td style="padding: 12px; border: 1px solid #e2e8f0; text-align: center; color: #ea580c; font-weight: bold;">${rc.oldRoom}</td>
                <td style="padding: 12px; border: 1px solid #e2e8f0; color: #059669; font-weight: bold;"><span style="color: #94a3b8; font-weight: normal; margin-right: 8px;">Moved to</span>${rc.newRoom}</td>
            </tr>
        `).join('');

        const html = `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 20px; background-color: #ffffff;">
                <table border="0" cellpadding="0" cellspacing="0" style="border-collapse: separate; border-spacing: 0; width: 100%; max-width: 800px; border: 2px solid #0f172a; border-radius: 12px; overflow: hidden;">
                    <thead>
                        <tr>
                            <th colspan="3" style="padding: 20px; text-align: center; background-color: #f8fafc; color: #0f172a; font-size: 18px; font-weight: 900; text-transform: uppercase; border-bottom: 2px solid #0f172a;">
                                ${report.displayDate}
                            </th>
                        </tr>
                        <tr style="background-color: #f1f5f9; text-transform: uppercase; font-size: 10px; font-weight: 900; letter-spacing: 0.1em; color: #64748b;">
                            <th style="padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0;">Classification</th>
                            <th style="padding: 12px; text-align: center; border-bottom: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; width: 120px;">Value</th>
                            <th style="padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0;">Additional Details</th>
                        </tr>
                    </thead>
                    <tbody style="color: #334155; font-size: 13px;">
                        <tr>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; font-weight: bold;">In-house rooms including Day Use</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.inHouseRooms}</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; color: #94a3b8; font-style: italic;">—</td>
                        </tr>
                        <tr>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; font-weight: bold;">Complimentary rooms</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.compRooms}</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; color: #94a3b8; font-style: italic;">—</td>
                        </tr>
                        <tr>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; font-weight: bold;">Ullivik No show assigned names</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.ullivikNoShowAssigned.count}</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; color: #475569;">${report.ullivikNoShowAssigned.details}</td>
                        </tr>
                        <tr>
                            <td style="padding: 12px; border-bottom: 2px solid #e2e8f0; border-right: 1px solid #f1f5f9; font-weight: bold;">Keep for Ullivik No show</td>
                            <td style="padding: 12px; border-bottom: 2px solid #e2e8f0; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.ullivikNoShowKeep.count}</td>
                            <td style="padding: 12px; border-bottom: 2px solid #e2e8f0; color: #475569;">${report.ullivikNoShowKeep.details}</td>
                        </tr>
                        <tr>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; font-weight: bold;">Regular guest no-show rooms</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.regularNoShow.count}</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; color: #475569;">${report.regularNoShow.details}</td>
                        </tr>
                        ${roomChangesHtml}
                        <tr>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; font-weight: bold;">Ullivik Departure</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.ullivikDeparture.count}</td>
                            <td style="padding: 12px; border-bottom: 1px solid #f1f5f9; color: #475569;">${report.ullivikDeparture.details}</td>
                        </tr>
                        <tr>
                            <td style="padding: 12px; border-right: 1px solid #f1f5f9; font-weight: bold;">Don’t Disturb Room</td>
                            <td style="padding: 12px; border-right: 1px solid #f1f5f9; text-align: center; background-color: #f8fafc; font-weight: 900;">${report.dndRooms.count}</td>
                            <td style="padding: 12px; color: #475569;">${report.dndRooms.details}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        `;
        const type = "text/html";
        const blob = new Blob([html], { type });
        const data = [new ClipboardItem({ [type]: blob })];
        navigator.clipboard.write(data);
        toast.success('Copied as formatted table');
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this report?')) return;
        try {
            await database.deleteDailyNightAuditReport(id);
            toast.success('Report deleted');
            if (selectedReport?.id === id) setSelectedReport(null);
        } catch (err) {
            toast.error('Failed to delete');
        }
    };

    const ReportTable = ({ report }: { report: DailyNightAuditReport }) => (
        <div className="bg-white dark:bg-slate-900 border-2 border-gray-900 dark:border-white shadow-xl overflow-hidden mt-6 font-mono text-sm max-w-2xl mx-auto">
            {/* Header */}
            <div className="border-b-2 border-gray-900 dark:border-white p-3 font-bold bg-gray-50 dark:bg-slate-800">
                {report.displayDate}
            </div>
            
            {/* Rows */}
            <div className="flex border-b border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 font-bold">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">Description</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700">Old Room / Count</div>
                <div className="w-48 p-2">New Room / Details</div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">No. of in-house rooms including Day Use</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.inHouseRooms}</div>
                <div className="w-48 p-2"></div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">No. of complimentary rooms</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.compRooms}</div>
                <div className="w-48 p-2"></div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">No. of Ullivik No show assigned names</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.ullivikNoShowAssigned.count}</div>
                <div className="w-48 p-2">{report.ullivikNoShowAssigned.details}</div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">No. of Keep for Ullivik No show</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.ullivikNoShowKeep.count}</div>
                <div className="w-48 p-2">{report.ullivikNoShowKeep.details}</div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">Regular guest no-show rooms</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.regularNoShow.count}</div>
                <div className="w-48 p-2">{report.regularNoShow.details}</div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">Ullivik Departure</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.ullivikDeparture.count}</div>
                <div className="w-48 p-2">{report.ullivikDeparture.details}</div>
            </div>
            <div className="flex border-b border-gray-300 dark:border-slate-700">
                <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700">Don’t Disturb Room</div>
                <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold">{report.dndRooms.count}</div>
                <div className="w-48 p-2">{report.dndRooms.details}</div>
            </div>
            
            {/* Dynamic Room Changes */}
            {report.roomChanges.map((rc, idx) => (
                <div key={idx} className="flex border-gray-300 dark:border-slate-700 bg-amber-50 dark:bg-amber-900/10 last:border-b-0 border-b">
                    <div className="flex-1 p-2 border-r border-gray-300 dark:border-slate-700 italic">Change rooms</div>
                    <div className="w-24 p-2 text-center border-r border-gray-300 dark:border-slate-700 font-bold text-indigo-600 dark:text-indigo-400">{rc.oldRoom}</div>
                    <div className="w-48 p-2 font-bold text-green-600 dark:text-green-400">{rc.newRoom}</div>
                </div>
            ))}
        </div>
    );

    if (currentLevel === 'history') {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between no-print">
                    <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Audit History</h3>
                    <button 
                        onClick={() => handleLevelChange('form')}
                        className="btn-secondary px-4 py-2 text-xs"
                    >
                        Back to Form
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-12"><Icons.RefreshCw className="w-8 h-8 animate-spin text-indigo-600" /></div>
                ) : reports.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 dark:bg-slate-900/50 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-800">
                        <Icons.History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">No records found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {reports.map(report => (
                            <div 
                                key={report.id}
                                onClick={() => {
                                    setSelectedReport(report);
                                    handleLevelChange('preview');
                                }}
                                className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 hover:border-indigo-500 cursor-pointer transition-all group shadow-sm flex items-center justify-between"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center text-indigo-600">
                                        <Icons.FileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 dark:text-white whitespace-nowrap">{report.displayDate.replace('Date — ', '')}</h4>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{report.inHouseRooms} rooms • By {report.createdBy}</p>
                                    </div>
                                </div>
                                <Icons.ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (currentLevel === 'preview' && selectedReport) {
        return (
            <div className="space-y-8 max-w-4xl mx-auto">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 no-print bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => handleLevelChange('form')}
                            className="w-10 h-10 rounded-full bg-gray-50 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-100 transition-colors"
                        >
                            <Icons.ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Report Preview</h2>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{selectedReport.displayDate}</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3">
                        {canExport && (
                            <>
                                <button onClick={() => copyAsText(selectedReport)} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold hover:bg-gray-50 transition-all flex items-center gap-2">
                                    <Icons.Copy className="w-4 h-4" /> Copy Text
                                </button>
                                <button onClick={() => copyAsTable(selectedReport)} className="px-4 py-2 bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-xl text-xs font-bold hover:opacity-90 transition-all flex items-center gap-2">
                                    <Icons.Table className="w-4 h-4" /> Copy Table
                                </button>
                            </>
                        )}
                        <button onClick={() => window.print()} className="px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold hover:bg-gray-50 transition-all flex items-center gap-2">
                            <Icons.Printer className="w-4 h-4" /> Print
                        </button>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-900 border-2 border-slate-900 dark:border-white shadow-2xl overflow-hidden rounded-[32px] max-w-2xl mx-auto print:shadow-none print:border-gray-500">
                    <div className="border-b-2 border-slate-900 dark:border-white p-6 font-black bg-slate-50 dark:bg-slate-800 text-center text-xl uppercase tracking-tighter">
                        {selectedReport.displayDate}
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                    <th className="p-4 text-left border-r border-slate-100 dark:border-slate-800">Classification</th>
                                    <th className="p-4 text-center border-r border-slate-100 dark:border-slate-800 w-28">Value</th>
                                    <th className="p-4 text-left">Additional Details</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                <tr className="group">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">In-house rooms including Day Use</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.inHouseRooms}</td>
                                    <td className="p-4 text-gray-400 italic">—</td>
                                </tr>
                                <tr className="group">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">Complimentary rooms</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.compRooms}</td>
                                    <td className="p-4 text-gray-400 italic">—</td>
                                </tr>
                                <tr className="group">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">Ullivik No show assigned names</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.ullivikNoShowAssigned.count}</td>
                                    <td className="p-4 font-medium text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{selectedReport.ullivikNoShowAssigned.details}</td>
                                </tr>
                                <tr className="group">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">Keep for Ullivik No show</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.ullivikNoShowKeep.count}</td>
                                    <td className="p-4 font-medium text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{selectedReport.ullivikNoShowKeep.details}</td>
                                </tr>
                                <tr className="group">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">Regular guest no-show rooms</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.regularNoShow.count}</td>
                                    <td className="p-4 font-medium text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{selectedReport.regularNoShow.details}</td>
                                </tr>
                                <tr className="group">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">Ullivik Departure</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.ullivikDeparture.count}</td>
                                    <td className="p-4 font-medium text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{selectedReport.ullivikDeparture.details}</td>
                                </tr>
                                <tr className="group border-b-2 border-slate-900 dark:border-slate-700">
                                    <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 transition-colors">Don’t Disturb Room</td>
                                    <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 font-black text-gray-900 dark:text-white">{selectedReport.dndRooms.count}</td>
                                    <td className="p-4 font-medium text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{selectedReport.dndRooms.details}</td>
                                </tr>
                                
                                {selectedReport.roomChanges.length > 0 && (
                                    <>
                                        <tr className="bg-blue-50/30 dark:bg-indigo-900/10">
                                            <td colSpan={3} className="px-4 py-2 text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest bg-blue-50/50 dark:bg-indigo-900/20">Room Exchanges / Swaps</td>
                                        </tr>
                                        {selectedReport.roomChanges.map((rc, idx) => (
                                            <tr key={idx} className="bg-blue-50/10 dark:bg-indigo-900/5 group border-l-4 border-indigo-500">
                                                <td className="p-4 font-bold text-gray-700 dark:text-gray-300 group-hover:translate-x-1 transition-transform italic">Room Change Action</td>
                                                <td className="p-4 text-center border-x border-slate-50 dark:border-slate-800 font-black text-orange-600 dark:text-orange-400">{rc.oldRoom}</td>
                                                <td className="p-4 font-black text-emerald-600 dark:text-emerald-400">
                                                    <span className="text-gray-400 mr-2 font-normal">Moved to</span>
                                                    {rc.newRoom}
                                                </td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {canGenerate && (
                     <div className="flex justify-center no-print mt-12 mb-8">
                        <button 
                            onClick={() => handleDelete(selectedReport.id)} 
                            className="text-red-500 hover:text-red-600 font-bold text-xs uppercase tracking-widest flex items-center gap-2"
                        >
                            <Icons.Trash2 className="w-4 h-4" /> Delete This Record
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 no-print bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm">
                <div>
                    <h2 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Report Configuration</h2>
                    <p className="text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest text-[10px]">{dateStrFormatted}</p>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => handleLevelChange('history')}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-gray-100 transition-all border border-transparent hover:border-gray-200"
                    >
                        <Icons.History className="w-4 h-4" />
                        View History
                    </button>
                    {canGenerate && (
                        <button 
                            onClick={handleReset}
                            className="flex items-center gap-2 px-4 py-2 text-gray-400 rounded-xl font-black uppercase tracking-widest text-[10px] hover:text-gray-600 transition-all"
                        >
                            <Icons.RefreshCw className="w-4 h-4" />
                            Reset
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-xl border border-gray-100 dark:border-slate-800">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Left Side: Main Data */}
                    <div className="space-y-6">
                        <div className="pb-4 border-b border-gray-100 dark:border-slate-800">
                            <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-6">Main Occupancy Data</h3>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">In-house rooms (inc. Day Use)</label>
                                    <input type="number" name="inHouseRooms" value={formData.inHouseRooms} onChange={handleChange} className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Complimentary rooms</label>
                                    <input type="number" name="compRooms" value={formData.compRooms} onChange={handleChange} className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all" />
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-6 px-1">
                                <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest">Room Changes</h3>
                                <button 
                                    onClick={addRoomChange}
                                    className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors flex items-center gap-1.5"
                                >
                                    <Icons.Plus className="w-3 h-3" /> Add Row
                                </button>
                            </div>
                            <div className="space-y-3">
                                {roomChanges.map((rc, index) => (
                                    <div key={index} className="flex items-center gap-3 group animate-in fade-in slide-in-from-left-2 transition-all">
                                        <div className="flex-1 grid grid-cols-2 gap-3">
                                            <input 
                                                type="text" 
                                                value={rc.oldRoom} 
                                                placeholder="From room"
                                                onChange={(e) => handleRoomChangeUpdate(index, 'oldRoom', e.target.value)} 
                                                className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs focus:ring-2 focus:ring-indigo-500 transition-all" 
                                            />
                                            <input 
                                                type="text" 
                                                value={rc.newRoom} 
                                                placeholder="To room"
                                                onChange={(e) => handleRoomChangeUpdate(index, 'newRoom', e.target.value)} 
                                                className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs focus:ring-2 focus:ring-indigo-500 transition-all" 
                                            />
                                        </div>
                                        <button 
                                            onClick={() => removeRoomChange(index)}
                                            className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-500 transition-colors"
                                        >
                                            <Icons.Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Grouped Sections */}
                    <div className="space-y-8 bg-gray-50/50 dark:bg-slate-900/50 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800">
                        <h3 className="text-xs font-black text-indigo-600 uppercase tracking-widest px-1">Specific Room Categories</h3>
                        
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Ullivik No Show Assigned Names</label>
                                <div className="flex gap-2">
                                    <input type="number" name="ullivikNoShowAssignedCount" value={formData.ullivikNoShowAssignedCount} onChange={handleChange} placeholder="Count" className="w-20 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                    <input type="text" name="ullivikNoShowAssignedRooms" value={formData.ullivikNoShowAssignedRooms} onChange={handleChange} placeholder="Room numbers (e.g. 101/102/103)" className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Keep for Ullivik No Show</label>
                                <div className="flex gap-2">
                                    <input type="number" name="ullivikNoShowKeepCount" value={formData.ullivikNoShowKeepCount} onChange={handleChange} placeholder="Count" className="w-20 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                    <input type="text" name="ullivikNoShowKeepRooms" value={formData.ullivikNoShowKeepRooms} onChange={handleChange} placeholder="Room numbers..." className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Regular Guest No-Show Rooms</label>
                                <div className="flex gap-2">
                                    <input type="number" name="regularNoShowCount" value={formData.regularNoShowCount} onChange={handleChange} placeholder="Count" className="w-20 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                    <input type="text" name="regularNoShowRooms" value={formData.regularNoShowRooms} onChange={handleChange} placeholder="Room numbers..." className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Ullivik Departure</label>
                                <div className="flex gap-2">
                                    <input type="number" name="ullivikDepartureCount" value={formData.ullivikDepartureCount} onChange={handleChange} placeholder="Count" className="w-20 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                    <input type="text" name="ullivikDepartureRooms" value={formData.ullivikDepartureRooms} onChange={handleChange} placeholder="Room numbers..." className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">Don’t Disturb Room</label>
                                <div className="flex gap-2">
                                    <input type="number" name="dndRoomsCount" value={formData.dndRoomsCount} onChange={handleChange} placeholder="Count" className="w-20 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                    <input type="text" name="dndRoomsRooms" value={formData.dndRoomsRooms} onChange={handleChange} placeholder="Room numbers..." className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl p-3 font-bold text-xs" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {canGenerate && (
                    <div className="mt-12 pt-8 border-t border-gray-100 dark:border-slate-800">
                        <button 
                            onClick={handleGenerate}
                            className="w-full bg-indigo-600 text-white py-6 rounded-[32px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-3 group"
                        >
                            Generate Report
                            <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NightAuditDailyReport;
